import { Chip } from '@heroui/react';

import { LogCard } from './shared';
import type { LogCardAccordionItemClasses } from './shared/accordionStyles';
import { AgentInputAddedConfig } from '../utils/logParser';

import i18n from '~/common/helpers/i18n';

interface InputCardProps {
  config: AgentInputAddedConfig;
  isExpanded?: boolean;
  onExpandedChange?: (expanded: boolean) => void;
  itemKey?: string;
  itemClasses?: LogCardAccordionItemClasses;
  className?: string;
}

/**
 * Component to display agent.input_added configuration in a structured, collapsible card
 */
export function InputCard({
  config,
  isExpanded,
  onExpandedChange,
  itemKey,
  itemClasses,
  className
}: InputCardProps) {
  const hasFiles = config.added_files.length > 0;

  const title = (
    <span className='text-sm font-semibold text-primary'>
      {i18n.t('scenes.build.execution.logs.input.label') as string}
    </span>
  );

  return (
    <LogCard
      title={title}
      textValue={i18n.t('scenes.build.execution.logs.input.label') as string}
      isExpanded={isExpanded}
      onExpandedChange={onExpandedChange}
      itemKey={itemKey}
      itemClasses={itemClasses}
      className={className}
    >
      {/* Added Files Section */}
      {hasFiles && (
        <div className='mb-4'>
          <h4 className='mb-2 text-xs font-semibold text-primary'>
            {i18n.t('scenes.build.execution.logs.input.added_files') as string}
          </h4>
          <div className='flex flex-wrap gap-2'>
            {config.added_files.map(file => (
              <Chip key={file} size='sm' variant='flat'>
                {file}
              </Chip>
            ))}
          </div>
        </div>
      )}

      {/* Added Tools Section */}
      <div className='mb-4'>
        <h4 className='mb-2 text-xs font-semibold text-primary'>
          {i18n.t('scenes.build.execution.logs.input.added_tools') as string}
        </h4>
        <div className='flex flex-wrap gap-2'>
          {config.tools.map(tool => (
            <Chip key={tool} size='sm' variant='flat'>
              {tool}
            </Chip>
          ))}
          {/* Content Retrieval as a tool tag - always shown */}
          <Chip
            size='sm'
            variant='flat'
            classNames={{
              base: config.content_retrieval ? '' : 'line-through opacity-60'
            }}
          >
            {
              i18n.t(
                'scenes.build.execution.logs.input.content_retrieval'
              ) as string
            }
          </Chip>
        </div>
      </div>

      {/* Content Retrieval Status */}
      <div className='mt-2 text-xs text-on-surface-variant'>
        {
          i18n.t(
            'scenes.build.execution.logs.input.content_retrieval_status'
          ) as string
        }
        {': '}
        <span
          className={
            config.content_retrieval
              ? 'font-medium text-success'
              : 'font-medium text-on-surface-variant'
          }
        >
          {config.content_retrieval
            ? (i18n.t('label.enabled') as string)
            : (i18n.t('label.disabled') as string)}
        </span>
      </div>
    </LogCard>
  );
}
